import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, ChevronRight, Calendar, RefreshCw, Layers, Boxes, Megaphone,
  ImageIcon, X, TrendingUp,
} from "lucide-react";
import { fmtCur, fmtNum, fmtPct, fmtRoas } from "./AgencyAdsPerformance";
import { AdsSyncBadge, AdsFreshness, AdsReconnectBanner } from "@/components/agency/AdsSyncBadge";

/**
 * META FASE 5 — Dettaglio Ads di un salone + drill-down
 * Tenant → Campaign → Ad Set → Ad → Creative.
 * Il periodo di attività creative è OSSERVATO (META 3), non lo status Meta.
 */
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "—");

const LEVELS = [
  { key: "campaigns", label: "Campagne", icon: Layers, next: "adsets", parentKey: "campaign_id" },
  { key: "adsets", label: "Ad Set", icon: Boxes, next: "ads", parentKey: "adset_id" },
  { key: "ads", label: "Ads", icon: Megaphone, next: "creatives", parentKey: "ad_id" },
  { key: "creatives", label: "Creative", icon: ImageIcon, next: null, parentKey: "creative_id" },
];

export default function AgencyAdsTenantDetail() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [since, setSince] = useState(daysAgo(30));
  const [until, setUntil] = useState(daysAgo(0));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [levelIdx, setLevelIdx] = useState(0);
  const [trail, setTrail] = useState([]); // [{level, id, name}]
  const [levelData, setLevelData] = useState(null);
  const [levelLoading, setLevelLoading] = useState(false);
  const [creative, setCreative] = useState(null);
  const sumRef = useRef(0);
  const lvlRef = useRef(0);

  const loadSummary = useCallback(async () => {
    const myId = ++sumRef.current;
    setLoading(true);
    try {
      const r = await api.get(`/agency/ads/tenant/${tenantId}/summary?start_date=${since}&end_date=${until}`);
      if (sumRef.current !== myId) return;
      setSummary(r.data);
    } catch { if (sumRef.current === myId) setSummary(null); }
    if (sumRef.current === myId) setLoading(false);
  }, [tenantId, since, until]);

  const loadLevel = useCallback(async () => {
    const lvl = LEVELS[levelIdx];
    const myId = ++lvlRef.current;
    setLevelLoading(true);
    try {
      let url = `/agency/ads/tenant/${tenantId}/${lvl.key}?start_date=${since}&end_date=${until}`;
      trail.forEach((t) => { url += `&${t.parentKey}=${encodeURIComponent(t.id)}`; });
      const r = await api.get(url);
      if (lvlRef.current !== myId) return;
      setLevelData(r.data.data || []);
    } catch { if (lvlRef.current === myId) setLevelData([]); }
    if (lvlRef.current === myId) setLevelLoading(false);
  }, [tenantId, since, until, levelIdx, trail]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadLevel(); }, [loadLevel]);

  const drillInto = (row) => {
    const lvl = LEVELS[levelIdx];
    if (lvl.key === "creatives") { setCreative(row); return; }
    if (!row.id || row.id === "__unattributed__") return;
    setTrail((t) => [...t, { level: lvl.key, id: row.id, name: row.name || row.id, parentKey: lvl.parentKey }]);
    setLevelIdx((i) => i + 1);
  };

  const jumpTo = (idx) => {
    setTrail((t) => t.slice(0, idx));
    setLevelIdx(idx);
  };

  const cur = summary?.currency || "EUR";
  const lvl = LEVELS[levelIdx];

  return (
    <div data-testid="agency-ads-tenant-detail" className="space-y-6">
      <button onClick={() => navigate("/admin/ads-performance")} data-testid="ads-detail-back"
        className="inline-flex items-center gap-1 text-sm font-bold text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)]">
        <ArrowLeft className="h-4 w-4" /> Tutti i saloni
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-black">{summary?.tenant_name || "Salone"}</h1>
          <p className="text-sm text-[color:var(--sp-text-muted)]">Periodo {fmtDate(since)} → {fmtDate(until)}</p>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <AdsSyncBadge sync={summary?.sync} testid="ads-detail-sync-badge" />
            <AdsFreshness sync={summary?.sync} />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => { setSince(daysAgo(d)); setUntil(daysAgo(0)); }}
              data-testid={`ads-detail-preset-${d}`}
              className="text-xs font-bold px-2.5 py-1.5 rounded-lg border border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)]">{d}gg</button>
          ))}
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="h-4 w-4 text-[color:var(--sp-text-muted)]" />
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)} data-testid="ads-detail-since" className="sp-input !py-1 !text-xs" />
            <span>→</span>
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="ads-detail-until" className="sp-input !py-1 !text-xs" />
          </div>
          <button onClick={() => { loadSummary(); loadLevel(); }} className="p-2 rounded-lg border border-[color:var(--sp-border)]">
            <RefreshCw className={`h-4 w-4 ${loading || levelLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* KPI sintetici (STESSA semantica META 4) */}
      <AdsReconnectBanner sync={summary?.sync} />
      {summary && (summary.status === "ok" || summary.status === "no_data_in_period") ? (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="ads-detail-kpis">
          <Kpi label="Spesa" value={fmtCur(summary.spend, cur)} badge={summary.spend_source === "meta" ? "Meta" : "Manuale"} />
          <Kpi label="Lead (Meta)" value={fmtNum(summary.leads)} sub={`${fmtNum(summary.meta_form_leads)} Form · ${fmtNum(summary.meta_pixel_leads)} Pixel`} />
          <Kpi label="CPL" value={fmtCur(summary.cpl, cur)} sub={`Memento ${fmtNum(summary.memento_leads)} · Δ Form ${summary.delta_form_leads ?? 0}`} />
          <Kpi label="Appuntamenti" value={fmtNum(summary.appointments_set)} />
          <Kpi label="Nuovi clienti" value={fmtNum(summary.new_customers)} />
          <Kpi label="CAC" value={fmtCur(summary.cac, cur)} />
          <Kpi label="Fatturato" value={fmtCur(summary.total_revenue, cur)} highlight />
          <Kpi label="ROAS Tot" value={fmtRoas(summary.total_roas)} sub={`FE ${fmtRoas(summary.front_end_roas)}`} highlight />
        </div>
      ) : summary ? (
        <div data-testid="ads-detail-status" className="sp-card p-8 text-center text-sm text-[color:var(--sp-text-muted)]">
          {summary.status === "meta_not_connected" ? "Questo salone non ha Meta collegato." : "Nessun account pubblicitario selezionato per questo salone."}
        </div>
      ) : null}

      {/* Breadcrumb drill-down */}
      <div className="flex items-center gap-1.5 flex-wrap text-sm" data-testid="ads-detail-breadcrumb">
        <button onClick={() => jumpTo(0)} className={`font-bold px-2 py-1 rounded-lg ${levelIdx === 0 ? "text-[color:var(--sp-brand)]" : "text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)]"}`}>Campagne</button>
        {trail.map((t, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <ChevronRight className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />
            <button onClick={() => jumpTo(i + 1)} className={`font-bold px-2 py-1 rounded-lg truncate max-w-[160px] ${i + 1 === levelIdx ? "text-[color:var(--sp-brand)]" : "text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)]"}`}>{t.name}</button>
          </span>
        ))}
      </div>

      {/* Tabella livello corrente */}
      <div className="rounded-2xl border border-[color:var(--sp-border)] overflow-hidden">
        <div className="px-4 py-3 bg-[color:var(--sp-subtle)] flex items-center gap-2">
          <lvl.icon className="h-4 w-4 text-[color:var(--sp-brand)]" />
          <span className="font-black text-xs uppercase tracking-wider">{lvl.label}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ads-detail-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-[color:var(--sp-text-muted)]">
                <th className="text-left px-4 py-2 font-black min-w-[200px]">Nome</th>
                <th className="text-right px-3 py-2 font-black">Spesa</th>
                <th className="text-right px-3 py-2 font-black">Impr.</th>
                <th className="text-right px-3 py-2 font-black">Click</th>
                <th className="text-right px-3 py-2 font-black">Lead Form</th>
                <th className="text-right px-3 py-2 font-black">Lead Pixel</th>
                <th className="text-right px-3 py-2 font-black">Lead Meta</th>
                <th className="text-right px-3 py-2 font-black">Memento Lead</th>
                <th className="text-right px-3 py-2 font-black" title="Lead Form Meta − webhook Instant Form Memento">Δ Form</th>
                <th className="text-right px-3 py-2 font-black">CPL</th>
                <th className="text-right px-3 py-2 font-black">Appt</th>
                <th className="text-right px-3 py-2 font-black">Clienti</th>
                <th className="text-right px-3 py-2 font-black">Fatturato</th>
                <th className="text-right px-3 py-2 font-black">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {levelLoading && <tr><td colSpan={14} className="px-4 py-6 text-center text-[color:var(--sp-text-muted)]">Caricamento…</td></tr>}
              {!levelLoading && (levelData || []).length === 0 && <tr><td colSpan={14} className="px-4 py-6 text-center text-[color:var(--sp-text-muted)]" data-testid="ads-detail-empty">Nessun dato nel periodo.</td></tr>}
              {!levelLoading && (levelData || []).map((row) => {
                const drillable = lvl.next && row.id && row.id !== "__unattributed__";
                const isCreativeLevel = lvl.key === "creatives";
                return (
                  <tr key={row.id || "unattr"} onClick={() => drillInto(row)}
                    data-testid={`ads-level-row-${row.id || "unattr"}`}
                    className={`border-t border-[color:var(--sp-border)] ${(drillable || isCreativeLevel) ? "cursor-pointer hover:bg-[color:var(--sp-brand)]/5" : ""}`}>
                    <td className="px-4 py-3 font-bold">
                      <div className="flex items-center gap-2">
                        <span className="truncate max-w-[220px]">{row.name || (row.id === "__unattributed__" ? "Non attribuito" : row.id)}</span>
                        {(drillable || isCreativeLevel) && <ChevronRight className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />}
                      </div>
                      {isCreativeLevel && row.first_activity_at && (
                        <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-0.5">
                          Attività osservata {fmtDate(row.first_activity_at)} → {fmtDate(row.last_activity_at)} · {row.activity_days} gg
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtCur(row.spend, row.currency || cur)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(row.impressions)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(row.clicks)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(row.meta_form_leads)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(row.meta_pixel_leads)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-bold">{fmtNum(row.meta_leads)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(row.memento_leads)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-[color:var(--sp-text-muted)]">{row.delta_form_leads ?? 0}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtCur(row.cpl, row.currency || cur)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(row.appointments_set)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(row.new_customers)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-bold">{fmtCur(row.total_revenue, row.currency || cur)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-bold">{fmtRoas(row.total_roas)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {creative && <CreativeDrawer creative={creative} cur={cur} trail={trail} onClose={() => setCreative(null)} />}
    </div>
  );
}

function Kpi({ label, value, sub, badge, highlight }) {
  return (
    <div className={`rounded-2xl border p-3 ${highlight ? "border-[color:var(--sp-brand)]/40 bg-[color:var(--sp-brand)]/5" : "border-[color:var(--sp-border)] bg-white"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{label}</div>
      <div className="text-xl font-black mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-0.5">{sub}</div>}
      {badge && <div className="mt-1 inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{badge}</div>}
    </div>
  );
}

function CreativeDrawer({ creative, cur, trail, onClose }) {
  const c = creative;
  const campaign = trail.find((t) => t.level === "campaigns")?.name;
  const adset = trail.find((t) => t.level === "adsets")?.name;
  const ad = trail.find((t) => t.level === "ads")?.name;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ads-creative-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl p-6 space-y-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-[color:var(--sp-brand)]/10 grid place-items-center"><ImageIcon className="h-5 w-5 text-[color:var(--sp-brand)]" /></div>
            <div>
              <h3 className="font-display text-lg font-black leading-tight">{c.name || "Creative"}</h3>
              <p className="text-xs text-[color:var(--sp-text-muted)]">Dettaglio creative</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="ads-creative-drawer-close" className="p-1.5 rounded-lg hover:bg-[color:var(--sp-subtle)]"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-1 text-xs text-[color:var(--sp-text-muted)]">
          {campaign && <div>Campagna: <span className="font-bold text-[color:var(--sp-text)]">{campaign}</span></div>}
          {adset && <div>Ad Set: <span className="font-bold text-[color:var(--sp-text)]">{adset}</span></div>}
          {ad && <div>Ad: <span className="font-bold text-[color:var(--sp-text)]">{ad}</span></div>}
        </div>

        {c.first_activity_at && (
          <div className="rounded-xl border border-[color:var(--sp-border)] p-3" data-testid="ads-creative-activity">
            <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-1"><TrendingUp className="h-3.5 w-3.5" /> Periodo attività osservato</div>
            <div className="text-sm font-bold">{fmtDate(c.first_activity_at)} → {fmtDate(c.last_activity_at)}</div>
            <div className="text-[11px] text-[color:var(--sp-text-muted)]">{c.activity_days} giorni con dati reali (non necessariamente consecutivi)</div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <DrawerStat label="Spesa" value={fmtCur(c.spend, c.currency || cur)} />
          <DrawerStat label="Lead Form" value={fmtNum(c.meta_form_leads)} />
          <DrawerStat label="Lead Pixel" value={fmtNum(c.meta_pixel_leads)} />
          <DrawerStat label="Meta Lead" value={fmtNum(c.meta_leads)} />
          <DrawerStat label="Memento Lead" value={fmtNum(c.memento_leads)} />
          <DrawerStat label="CPL" value={fmtCur(c.cpl, c.currency || cur)} />
          <DrawerStat label="Appuntamenti" value={fmtNum(c.appointments_set)} />
          <DrawerStat label="Nuovi clienti" value={fmtNum(c.new_customers)} />
          <DrawerStat label="CAC" value={fmtCur(c.cac, c.currency || cur)} />
          <DrawerStat label="Fatturato FE" value={fmtCur(c.front_end_revenue, c.currency || cur)} />
          <DrawerStat label="Fatturato BE" value={fmtCur(c.back_end_revenue, c.currency || cur)} />
          <DrawerStat label="Fatturato Tot" value={fmtCur(c.total_revenue, c.currency || cur)} highlight />
          <DrawerStat label="ROAS FE" value={fmtRoas(c.front_end_roas)} />
          <DrawerStat label="ROAS Tot" value={fmtRoas(c.total_roas)} highlight />
        </div>
      </div>
    </div>
  );
}

function DrawerStat({ label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-2.5 ${highlight ? "border-[color:var(--sp-brand)]/40 bg-[color:var(--sp-brand)]/5" : "border-[color:var(--sp-border)]"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{label}</div>
      <div className="text-base font-black mt-0.5">{value}</div>
    </div>
  );
}
